import { Base64 } from 'js-base64';
import { cloneDeep, throttle, trimStart, trimEnd } from 'lodash-es';
import qs from 'qs';
import platform from './platform';

const ELEMENT_ID_PREFIX = 'CEIBA_ELEMENT_'; // 自动生成ID的前缀
const elementIdMap = {};
let elementIdCount = 0;
// 特殊透传参数业务
const AST_SPECIAL_PASS_THROUGH_URL_PARAM = ['tgclub', 'commercial'];

function getFields(object, querys) {
  if (querys.length > 1) {
    const key = querys.shift();
    return getFields(object[key], querys);
  }
  let field = null;
  if (querys[0] in object) {
    field = object[querys[0]];
  }
  return field;
}

/**
 * 规则算子
 */
const RULE_MAP = {
  '=': (left, right) => left === right,
  '>': (left, right) => left > right,
  '>=': (left, right) => left >= right,
  '<=': (left, right) => left <= right,
  '<': (left, right) => left < right,
  contain: (left, right) => `${left}`.indexOf(`${right}`) !== -1,
  exist: (left, right) => left?.[right],
};

/**
 * 检查是否满足规则
 * @param {*} obj
 * @param {*} rule
 * @returns
 */
function checkRule(obj, rule) {
  const context = obj || {};
  context.window = window;
  const { key, op, value } = rule || {};
  if (!RULE_MAP[op]) {
    throw new Error(`op invalid, op=${op}`);
  }
  if (key && op && value) {
    if (key.indexOf('$$.') !== -1) {
      // $$.actInfo.game_code
      const querys = key.replace('$$.', '').split('.');
      if (querys.length > 0) {
        const result = getFields(context, querys);
        return RULE_MAP[op](result, value);
      }
    }
  }
  return false;
}

export const getParamType = obj => (obj === null ? String(obj) : Object.prototype.toString.call(obj).replace(/\[object\s+(\w+)\]/i, '$1') || 'object');

/**
 * 判断对象是否日期
 * @param {object} object 对象
 * @return {bool} 是/否
 */
export const isDate = obj => getParamType(obj).toLowerCase() === 'date';

/**
 * 判断对象是否字符串
 * @param {object} object 对象
 * @return {bool} 是/否
 */
export const isString = obj => getParamType(obj).toLowerCase() === 'string';

/**
 * 将object类型的cookie配置序列化成字符串
 * @param {Object} o cookie的配置
 * @returns 字符串序列化后的配置
 */
const formatCookieOptions = (o) => {
  const { path, domain, expires, secure, sameSite } = o;
  const sameSiteValue = ['none', 'lax', 'strict'].indexOf((sameSite || '').toLowerCase()) > -1 ? sameSite : null;
  return [
    typeof path === 'undefined' || path === null ? '' : `;path=${path}`,
    typeof domain === 'undefined' || domain === null ? '' : `;domain=${domain}`,
    typeof expires === 'undefined' || expires === null ? '' : `;expires=${expires.toUTCString()}`,
    typeof secure === 'undefined' || secure === false ? '' : ';secure',
    sameSiteValue === null ? '' : `;SameSite=${sameSiteValue}`,
  ].join('');
};

/**
 * 设置一个cookie
 * @param {string} key
 * @param {string} data
 * @param {object} options cookie配置
 */
const setCookie = (key, data = '', options = {}) => {
  if (typeof document !== 'undefined' && typeof document.cookie !== 'undefined') {
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(data)}${formatCookieOptions(options)}`;
  }
};


/**
 * @description 清除cookie
 * @param {String} key key
 * @param {String} domain cookie域
 * @returns
 */
function clearCookie(key, hostDomain = location?.hostname, path = location?.pathname, cookieOptions = {}) {
  // Deep remove a cookie.
  const domains = ['', '/'];
  const hostname = hostDomain?.split('.') || [];
  while (hostname.length > 1) {
    domains.push(hostname.join('.'));
    hostname.shift();
  }

  // delete cookies from all domains and all paths
  for (let i = 0; i < domains.length; i++) {
    const pathname = path?.split('/') || [];
    let levelPath = ''; // 当前要清理的path
    while (pathname.length > 0) {
      const p = pathname.shift();
      levelPath += (levelPath === '/' ? '' : '/') + p;
      const options = {
        ...cookieOptions,
        path: levelPath,
        domain: domains[i],
        expires: new Date(0),
      };
      setCookie(key, '', options);
    }
  }
}

/**
 * 解析cookie string为对象
 * @param {string} str cookie string
 * @returns [key: string]: string
 */
const parseCookieString = (str) => {
  if (!str || str.length === 0) {
    return {};
  }
  const cookies = {};
  const pairs = str.split(';');
  for (let i = 0; i < pairs.length; i++) {
    const idx = pairs[i].indexOf('=');
    if (idx < 0) {
      continue;
    }
    let key = pairs[i].substring(0, idx).replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, ''); // trim whitespace
    let value = pairs[i].substring(idx + 1, pairs[i].length);
    try {
      key = decodeURIComponent(key);
    } catch (e) {
      // do nothing
    }
    try {
      value = decodeURIComponent(value);
    } catch (e) {
      // do nothing
    }
    cookies[key] = value;
  }
  return cookies;
};

/**
 * 获取cookie string
 * @returns cookie string
 */
const getCookieString = () => (typeof document === 'undefined' || typeof document.cookie === 'undefined' ? '' : document.cookie);

/**
 * 设置url中的某个query参数
 * @param {string} key 参数key
 * @param {string} value 参数值
 * @param {string} link 需要替换的链接地址,默认为当前页面的url
 * @returns 参数设置完成后的完成url
 */
const setUrlParam = (key, value, link = '') => {
  let targetLink = link;
  if (key && (value !== null && typeof value !== 'undefined')) {
    // 只有当key和value都有值的时候才具体set参数,避免空参数
    let targetUrl = '';
    let targetSearch = '';
    let targetSearchNotEncode = '';
    let targetHash = '';
    if (targetLink === '') {
      targetLink = location?.href;
      targetUrl = location?.origin + location?.pathname;
      targetSearch = location?.search;
      targetHash = location?.hash;
    } else {
      if (targetLink.includes('#wechat_redirec') && targetLink.includes('__biz')) {
        // 微信公众号落地页单独处理
        targetHash = '';
      } else {
        // 分离hash段
        [targetLink, targetHash] = targetLink.split('#');
        if (targetHash) {
          targetHash = `#${targetHash}`;
        } else {
          targetHash = '';
        }
      }
      // 分离原始参数段
      [targetLink, targetSearch] = targetLink.split('?');
      targetUrl = targetLink;
    }
    // 处理已有的search参数
    const params = qs.parse(targetSearch);
    // 覆盖新值
    params[key] = value;
    // 重新序列化
    const paramsNotEncode = {};
    ['via_source', '__biz'].forEach((nEncodeKey) => {
      if (Object.prototype.hasOwnProperty.call(params, nEncodeKey)) {
        // 参数里存在不需要 encode 的 key，单独摘出
        paramsNotEncode[nEncodeKey] = params[nEncodeKey];
        delete params[nEncodeKey];
      }
    });
    targetSearch = qs.stringify(params);
    targetSearchNotEncode = qs.stringify(paramsNotEncode, { encode: false });
    // 拼接
    if (targetSearchNotEncode.length > 0) {
      targetSearch += `&${targetSearchNotEncode}`;
    }
    // 防止前者为空，去除开头和结尾的&字符
    trimStart(targetSearch, '&');
    trimEnd(targetSearch, '&');
    if (targetSearch) {
      targetSearch = `?${targetSearch}`;
    } else {
      targetSearch = '';
    }
    return `${targetUrl}${targetSearch}${targetHash}`;
  }
  return targetLink;
};

/**
 * 获取URL指定参数
 * @param {string} key 需要获取的query param的key
 * @param {string} url 获取参数的对象url,默认为location.href
 * @returns
 */
const getQueryParams = (key, link) => {
  const url = link || location.href;
  let targetUrl = '';
  let targetSearch = '';
  // 分离hash段
  [targetUrl] = url.split('#');
  // 分离 query 段
  [targetUrl, targetSearch] = targetUrl.split('?');
  // 处理已有的search参数
  const params = qs.parse(targetSearch);
  let queryValue = '';
  if (Array.isArray(params[key]) && params[key]?.length > 0) {
    // 存在重复参数
    queryValue = params[key].pop();
  } else {
    queryValue = params[key];
  }
  return queryValue;
};


export const tool = {
  /**
   * 获取cookie
   * @param {string} key
   * @returns
   */
  getCookie: (key) => {
    const cookies = parseCookieString(getCookieString());
    return Object.prototype.hasOwnProperty.call(cookies, key) ? cookies[key] : null;
  },
  /**
   * 设置一个cookie
   */
  setCookie,
  /**
   * @deprecated
   * 设置单个或者多个cookie 分号隔开,需要调用方手动拼接成一个长字符串
   * @param {string} str
   * @returns
   */
  setCookies: (str) => {
    const cookies = str.split(';');
    if (!cookies && !cookies.length) return;
    cookies.forEach(ck => (document.cookie = ck.replace(/\s$/, '')));
  },
  /**
   * 清除一个cookie
   */
  clearCookie,
  /**
   * deep清除所有cookie
   * qq客户端内不清除skey和uin
   * @returns
   */
  clearAllCookie: () => {
    // Deep clear all cookies.
    const cookies = parseCookieString(getCookieString());
    const keys = [...Object.keys(cookies).sort()];
    const ptCookies = ['skey', 'uin'];
    for (let i = 0; i < keys.length; i++) {
      if (platform.isQQ && ptCookies.includes(keys[i])) {
        // qq客户端内,不删除skey和uin这两个cookie
        continue;
      }
      clearCookie(keys[i]);
    }
  },
  /**
   * 设置url中的某个query参数
   * @param {string} key 参数key
   * @param {string} value 参数值
   * @param {string} link 需要替换的链接地址,默认为当前页面的url
   * @returns 参数设置完成后的完成url
   */
  setUrlParam,
  /**
   * 设置url中指定的参数
   * @param {*} params
   * @param {*} link
   * @returns
   */
  setUrlParams: (params, link) => {
    let url = link;
    Object.keys(params).forEach((k) => {
      url = setUrlParam(k, params[k], url);
    });
    return url;
  },
  /**
   * 动态加载JS
   * @param {string} id
   * @param {string} url
   * @param {function} callback
   */
  loadScript: (id, url, callback) => {
    const target = document.getElementById(id);
    if (target) {
      callback?.();
    } else {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = id;
      script.src = url;
      script.onload = readystateFun;
      script.onreadystatechange = readystateFun;
      function readystateFun() {
        if (
          !script.readyState
          || script.readyState === 'loaded'
          || script.readyState === 'complete'
        ) {
          callback?.();
        }
      }
      script.src = url;
      const head = document.getElementsByTagName('head')[0];
      head.appendChild(script);
    }
  },
  /**
   * 动态加载style
   * @param {*} url
   * @param {*} callback
   */
  loadStyle: (url, callback) => {
    if (typeof window !== 'undefined') {
      const target = window.document.querySelector(`link[href="${url}"]`);
      if (target) {
        callback?.();
      } else {
        const node = window.document.createElement('link');
        node.rel = 'stylesheet';
        node.href = url;
        document.getElementsByTagName('head')[0].appendChild(node);
      }
    }
  },
  // 获取新的ID
  genElementID: () => {
    let id = void 0;
    elementIdCount = elementIdCount + 1;
    do {
      id = ELEMENT_ID_PREFIX + elementIdCount;
      if (!elementIdMap[elementIdCount] && !document.getElementById(id)) break;
      elementIdMap[elementIdCount] = 1; // 占用全局ID防止意外重复
    } while (1);
    return id;
  },
  // Base64
  Base64,
  /**
   * 图片加载完成判断
   */
  loadImage: url => new Promise((resolve) => {
    let img = new Image();
    img.onload = function () {
      if (this.complete === true) {
        resolve(true);
        img = null;
      }
    };
    img.onerror = function () {
      resolve(false);
      img = null;
    };
    img.src = url;
  }),
  /**
   * 转换px 到rem
   * @param {*} style
   * @returns
   */
  transformStyle: (style) => {
    if (!style) {
      return {};
    }
    const styleObj = { ...style };
    const results = {};
    Object.entries(styleObj).forEach(([key, value]) => {
      if (value && /^[-]?[0-9]*[.]?[0-9]*px$/.test(value)) {
        results[key] = `${parseInt(value.substring(0, value.length - 2), 10) / 100}rem`;
      } else {
        results[key] = value;
      }
    });
    return results;
  },
  /**
  * 转换px 到rem
  * @param {*} value
  * @returns
  */
  transformToRem: (value) => {
    if (value && /^[-]?[0-9]*[.]?[0-9]*px$/.test(value)) {
      return `${parseInt(value.substring(0, value.length - 2), 10) / 100}rem`;
    }
    return value;
  },
  /**
   * @deprecated 请直接使用lodash-es内的throttle方法
   * 函数节流,直接复用lodash的
   * @param {Function} func 需要节流的函数
   * @param {Number} delay 延迟毫秒
   * @returns 节流后的函数
   */
  funcThrottle: (func, delay) => throttle(func, delay),
  cloneDeep,

  /**
   * 生成uuid 6ae9ebd6-d7d7-5a39-e27c-807daf66d8cd
   */
  uuid: () => {
    const S4 = function () {
      return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    };
    return (`${S4() + S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`);
  },

  /**
   * 通过渠道区分列表获取销售渠道ID
   * @param {*} options
   * [{
      label: "DNF助手DNF专区",
      value:"1",
      rule_type: "AND",
      rules: [
        {
          key: "$$.window",
          op: "exist", // 等于，大于，大于等于，小于等于，小于，包含，存在
          value: "Tgclub"
        },
        {
          key: "$$.window.navigator.userAgent",
          op: "contain",
          value: "gamehelper"
        },
        {
          key: "$$.actInfo.game_code",
          op: "=",
          value: "dnf"
        }
      ]
    }]
   */
  getRuleValue: (obj, options) => {
    for (const item of options) {
      const ruleType = item.rule_type;
      const rules = item.rules || [];
      if (ruleType === 'OR') {
        // OR
        for (const rule of rules) {
          if (checkRule(obj, rule)) {
            return item.value;
          }
        }
      } else if (ruleType === 'AND') {
        // AND
        let hit = true;
        for (const rule of rules) {
          if (!checkRule(obj, rule)) {
            hit = false;
            break;
          }
        }
        if (hit) return item.value;
      } else if (ruleType === 'LOGIC') {
        let expression = item.logic; // 表达式
        const ruleResMapping = {};
        let hit = false;
        for (const rule of rules) {
          ruleResMapping[rule.alias] = checkRule(obj, rule);
        }

        for (const ruleMap of Object.entries(ruleResMapping)) {
          expression = expression.replaceAll(ruleMap[0], ruleMap[1].toString());
        }
        try {
          // eslint-disable-next-line no-eval
          hit  = eval(expression);
        } catch (e) {
          console.error('run expression =', expression, 'error =', e);
        }
        if (hit) return item.value;
      }
    }
    return '';
  },
  /*
  * 获取A.B.C路径下的值
  */
  getFields,
  /*
  * 校验是否满足规则
  */
  checkRule,
  //  监听区域内滚动
  listenElementScrollEvent: (eleRef, handleScrollEvent) => {
    if (eleRef) {
      eleRef.addEventListener('scroll', handleScrollEvent);
    }
  },
  //  删除监听区域内滚动
  removeElementScrollEvent: (eleRef, handleScrollEvent) => {
    if (eleRef) {
      eleRef.removeEventListener('scroll', handleScrollEvent);
    }
  },
  // 判断是否滚动到底部
  checkIsScrollEnd: (eleRef) => {
    if (!eleRef) {
      return false;
    }
    // scrollTop 滚动条的位置
    // scrollHeight 元素内容的高度
    // clientHeight 元素的像素高度，包含元素的高度+内边距
    const { scrollTop, clientHeight, scrollHeight } = eleRef;
    // 滚动位置
    const bodyHeight = Math.max(clientHeight, scrollHeight);
    const res = bodyHeight <= scrollTop + clientHeight + 2;
    return res;
  },
  /**
   * 通过组件实例id找到目标组件对象,并返回*引用对象*
   * @param {Array} componentList 树状组件列表
   * @param {String} uuid 组件实例uuid
   * @returns 目标组件对象(引用)
   */
  findComponentById: (componentList, uuid) => {
    let targetComponent = undefined;

    // 深度遍历
    // eslint-disable-next-line
    componentLoop:
    for (let i = 0; i < componentList.length; ++i) {
      // 存在子组件,继续向下遍历
      if (componentList[i].childrenSlots && Object.keys(componentList[i].childrenSlots).length > 0) {
        // eslint-disable-next-line
          for (const slot in componentList[i].childrenSlots) {
          // 遍历各个slot
          if (Object.hasOwnProperty.call(componentList[i].childrenSlots, slot)) {
            const firstLevelList = componentList[i].childrenSlots[slot];
            for (let firstIndex = 0; firstIndex < firstLevelList.length; firstIndex++) {
              // 遍历slot下的组件列表
              const firstLevelComponent = firstLevelList[firstIndex];
              // 存在子组件的子组件,继续向下遍历
              if (firstLevelComponent.childrenSlots && Object.keys(firstLevelComponent.childrenSlots).length > 0) {
                // eslint-disable-next-line
                  for (const secondSlot in firstLevelComponent.childrenSlots) {
                  // 遍历子组件的的slot
                  if (Object.hasOwnProperty.call(firstLevelComponent.childrenSlots, secondSlot)) {
                    const secondLevelList = firstLevelComponent.childrenSlots[secondSlot];
                    for (let secondIndex = 0; secondIndex < secondLevelList.length; secondIndex++) {
                      // 遍历子组件slot下的组件列表
                      const secondLevelComponent = secondLevelList[secondIndex];
                      if (secondLevelComponent.id === uuid) {
                        targetComponent = secondLevelComponent;
                        break componentLoop;
                      }
                    }
                  }
                }
              }
              if (firstLevelComponent.id === uuid) {
                targetComponent = firstLevelComponent;
                break componentLoop;
              }
            }
          }
        }
      }
      if (componentList[i].id === uuid) {
        targetComponent = componentList[i];
        break componentLoop;
      }
    }
    return targetComponent;
  },
  /**
  * 获取日期
  * @param {string} sep 分隔符 默认为-
  * @return {string} yyyy-mm-dd
  */
  toDateString: function toDateString(nd, ...args) {
    const a = [];
    const dt = isDate(nd) ? nd : new Date();
    const m = dt.getMonth() + 1;
    const d = dt.getDate();
    const backupSep = isString(args[0]) ? args[0] : '-';
    const sep = args[1] ? args[1] : backupSep;
    a.push(dt.getFullYear());
    a.push(m.toString().length < 2 ? '0'.concat(m) : m);
    a.push(d.toString().length < 2 ? '0'.concat(d) : d);
    return a.join(sep);
  },
  /**
  * 获取日期和时间
  * @param {string} sep 分隔符 默认为-
  * @return {string} yyyy-mm-dd hh:ii:ss
  */
  toDateTimeString: function toDateTimeString(nd, ...args) {
    const dt = isDate(nd) ? nd : new Date();
    const h = dt.getHours();
    const i = dt.getMinutes();
    const s = dt.getSeconds();
    const a = [];
    a.push(h.toString().length < 2 ? '0'.concat(h) : h);
    a.push(i.toString().length < 2 ? '0'.concat(i) : i);
    a.push(s.toString().length < 2 ? '0'.concat(s) : s);
    return ''.concat(tool.toDateString.apply(this, [nd, ...args]), ' ').concat(a.join(':'));
  },
  /**
   * 获取服务器时间
   * @param {String} serverUrl 服务器地址，默认为window.location.href
   * @returns {Date}
   */
  getSeverDateTime: (serverUrl = window.location.href) => {
    const xhr = new XMLHttpRequest();
    xhr.open('HEAD', serverUrl, false);
    xhr.send();
    const d = new Date(xhr.getResponseHeader('Date'));
    return d;
  },
  getParamType,

  /**
   * 判断对象是否日期
   * @param {object} object 对象
   * @return {bool} 是/否
   */
  isDate,

  /**
   * 判断对象是否字符串
   * @param {object} object 对象
   * @return {bool} 是/否
   */
  isString,
  /**
   * 获取当前页面url,并删除指定key的参数
   * @param {string} key 要删除的key名
   * @returns 新的url
   */
  delHrefParam: (key) => {
    const loca = window.location;
    const baseUrl = `${loca.origin + loca.pathname}?`;
    const query = loca.search.substring(1);
    if (query.indexOf(key) > -1) {
      const obj = {};
      const arr = query.split('&');
      for (let i = 0; i < arr.length; i++) {
        arr[i] = arr[i].split('=');
        const [oldKey, value] = arr[i];
        obj[oldKey] = value;
      }
      delete obj[key];
      const url = baseUrl + JSON.stringify(obj).replace(/["{}]/g, '')
        .replace(/:/g, '=')
        .replace(/,/g, '&');
      return url;
    }
  },
  /**
   * @deprecated
   * 拼写有误，后续直接调用getQueryParams
   * 兼容旧版
   */
  getQueryparams: getQueryParams,
  getQueryParams,
  /**
   * 当前打开的活动链接的参数透传到分享或跳转的链接上
   * @param {String} url 分享或跳转的链接
   * @param {Array} params 需要透传的参数，默认透传['via', 'trace_id', 'via_source']
   * @param {Boolean} isShare 处理的是否是分享链接
   * @returns 透传参数后新的url
   */
  passThroughUrlParam: (url, params = [], isShare = false) => {
    let newUrl = url;
    const ceibaAst = window?.CEIBA_CURRENT_AST || '';
    params.forEach((k) => {
      if (ceibaAst === 'commercial' && ['inviteCode', 'commander_id'].includes(k)) {
        // 商业化业务,新增两个特殊参数不做处理,固定在循坏下面做特殊处理
        return;
      }
      if (['trace_id', 'via_source', 'via'].includes(k)) {
        // 两个特殊参数不做处理,在循坏下面做特殊处理
        return;
      }
      const paramsValue = getQueryParams(k);
      if (paramsValue) {
        newUrl = setUrlParam(k, paramsValue, newUrl);
      }
    });
    // 处理via_source和trace_id还有 via的透传逻辑
    // 从全局对象中获取上报的trace_id
    let traceId = window.ceibaTraceId;
    if (isShare) {
      if (traceId.includes('_UPBE_')) {
        // 二次分享的时候先删除之前的分销 id
        [traceId] = traceId.split('_UPBE_');
      }
      // 如果生成的是分享链接地址
      if (window.commissionSellingSuffix) {
        // 从全局获取分销 code，如果有分销 code 则拼接上分销 code
        traceId = `${traceId}_${window.commissionSellingSuffix}`;
        // 重置用过的分销 code
        window.commissionSellingSuffix = '';
      } else {
        traceId = `${traceId}S`;
      }
    }
    newUrl = setUrlParam('trace_id', traceId, newUrl);
    // 如果处理的是分享链接，via_source 只保留活动纯净版
    const viaSource = isShare ? window.ceibaViaSource : window.viaSource;
    newUrl = setUrlParam('via_source', viaSource, newUrl);
    // 只有存在上游 via 的时候才做透传
    const LOW_PRIORITY_VIA = ['undefined', 'UNDEFINED', 'null', 'NULL', 'open', 'OPEN', 'other', 'OTHER', ''];
    const via = getQueryParams('via', url);
    if (typeof window.ceibaVia !== 'undefined') {
      // 判断上游via是否在黑名单
      if (LOW_PRIORITY_VIA.includes(window.ceibaVia)) {
        if (LOW_PRIORITY_VIA.includes(via)) {
          newUrl = setUrlParam('via', window.ceibaVia, newUrl);
        } else {
          if (typeof via === 'undefined') {
            newUrl = setUrlParam('via', window.ceibaVia, newUrl);
          } else {
            newUrl = setUrlParam('via', via, newUrl);
          }
        }
      } else {
        newUrl = setUrlParam('via', window.ceibaVia, newUrl);
      }
    }
    // 仅心悦|商业化业务,默认透传inviteCode和commander_id
    if (AST_SPECIAL_PASS_THROUGH_URL_PARAM.includes(ceibaAst)) {
      // 如果链接上有commander_id时透传
      const commanderID = getQueryParams('commander_id');
      if (commanderID) {
        newUrl = setUrlParam('commander_id', commanderID, newUrl);
      }
      // 如果链接上有inviteCode,透传.
      const inviteCode = getQueryParams('inviteCode');
      if (inviteCode) {
        newUrl = setUrlParam('inviteCode', inviteCode, newUrl);
      }
    }
    return newUrl;
  },
};

export default tool;
